import { createSelector } from '@reduxjs/toolkit'

// Base selectors
export const selectLoadingStatus = state => state?.SubscribedInstitutes?.loading
export const selectError = state => state?.SubscribedInstitutes?.error

// Count selectors
export const selectActiveAssessmentCount = state =>
  state?.SubscribedInstitutes?.activeAssessmentCount ?? 0
export const selectAssessmentCompletedCount = state =>
  state?.SubscribedInstitutes?.assessmentCompletedCount ?? 0

// Filters selector
export const selectFilters = state => state?.SubscribedInstitutes?.filters

// Active Assessments selectors
export const selectActiveAssessments = state =>
  state?.SubscribedInstitutes?.activeAssessments ?? []

export const selectActiveAssessmentsPagination = state =>
  state?.SubscribedInstitutes?.activeAssessmentsPagination ?? {
    page: 0,
    limit: 10,
    totalPages: 1,
    totalItems: 0,
    currentCount: 0,
  }

export const selectActiveAssessmentsMetrics = state =>
  state?.SubscribedInstitutes?.activeAssessmentsMetrics ?? {
    total: 0,
    completed: 0,
    pending: 0,
  }

// Completed Assessments selectors
export const selectCompletedAssessments = state =>
  state?.SubscribedInstitutes?.completedAssessments ?? []

export const selectCompletedAssessmentsPagination = state =>
  state?.SubscribedInstitutes?.completedAssessmentsPagination ?? {
    page: 0,
    limit: 10,
    totalPages: 1,
    totalItems: 0,
    currentCount: 0,
  }

export const selectCompletedAssessmentsMetrics = state =>
  state?.SubscribedInstitutes?.completedAssessmentsMetrics ?? {
    total: 0,
    completedOnTime: 0,
    completedLate: 0,
    infoCards: {
      totalCandidates: 0,
      totalAssessmentsSent: 0,
      totalAssessmentsTaken: 0,
      totalPracticeTests: 0,
    }
  }

// Selectors for Assessment Details and student data
export const selectStudentDataSent = state =>
  state?.SubscribedInstitutes?.studentData?.sent ?? []

export const selectStudentDataPending = state =>
  state?.SubscribedInstitutes?.studentData?.pending ?? []

export const selectStudentDataInProgress = state =>
  state?.SubscribedInstitutes?.studentData?.inProgress ?? []

export const selectStudentDataDroppedOff = state =>
  state?.SubscribedInstitutes?.studentData?.droppedOff ?? []

export const selectStudentDataCompleted = state =>
  state?.SubscribedInstitutes?.studentData?.completed ?? []

export const selectStudentDataLoading = state =>
  state?.SubscribedInstitutes?.studentDataLoading ?? false

export const selectStudentDataPagination = state =>
  state?.SubscribedInstitutes?.studentDataPagination ?? null

export const selectStudentDataError = state =>
  state?.SubscribedInstitutes?.studentDataError ?? null

export const selectAssessmentFilters = state =>
  state?.SubscribedInstitutes?.assessmentFilters ?? {
    passingYear: '',
    specialization: '',
  }

export const selectChartFilters = state =>
  state?.assessment?.chartFilters ?? {
    cefr: null,
    aptitudeLevel: null,
    consistency: null,
    assessmentType: null,
  }

// Utility selector to get student data by status type
export const selectStudentDataByStatus = (state, statusType) => {
  switch (statusType) {
    case 'sent':
      return selectStudentDataSent(state)
    case 'pending':
      return selectStudentDataPending(state)
    case 'inprogress':
      return selectStudentDataInProgress(state)
    case 'droppedoff':
      return selectStudentDataDroppedOff(state)
    case 'completed':
      return selectStudentDataCompleted(state)
    default:
      return []
  }
}

// FIXED: Activity Tracker selectors - make sure these point to the correct state
export const selectActivityData = (state) =>
  state?.SubscribedInstitutes?.activityData || []

export const selectActivityLoading = (state) =>
  state?.SubscribedInstitutes?.activityLoading || false

export const selectActivityError = (state) =>
  state?.SubscribedInstitutes?.activityError || null

export const selectActivityDateRange = (state) =>
  state?.SubscribedInstitutes?.activityDateRange || { startDate: null, endDate: null }

export const selectPracticeScores = (state) => {
  // Changed from state.assessment to state.SubscribedInstitutes
  const scores = state.SubscribedInstitutes?.practiceScores?.data || []
  return scores
}

export const selectPracticeScoresLoading = (state) => {
  // Changed from state.assessment to state.SubscribedInstitutes
  const loading = state.SubscribedInstitutes?.practiceScores?.loading || false
  return loading
}

export const selectPracticeScoresError = (state) => {
  // Changed from state.assessment to state.SubscribedInstitutes
  const error = state.SubscribedInstitutes?.practiceScores?.error || null
  return error
}

// FIXED: Changed from state.assessment to state.SubscribedInstitutes
export const selectCompletedPracticeAssessments = state =>
  state?.SubscribedInstitutes?.completedPracticeAssessments?.data ?? []

export const selectCompletedPracticeAssessmentsLoading = state =>
  state?.SubscribedInstitutes?.completedPracticeAssessments?.loading ?? false

export const selectCompletedPracticeAssessmentsError = state =>
  state?.SubscribedInstitutes?.completedPracticeAssessments?.error ?? null

export const selectCompletedPracticeAssessmentsPagination = state =>
  state?.SubscribedInstitutes?.completedPracticeAssessments?.pagination ?? null

// Add this new selector for calculating total practice hours
export const selectTotalPracticeHours = (state) => {
  const completedAssessments = state?.SubscribedInstitutes?.completedPracticeAssessments?.data ?? []

  const totalHours = completedAssessments.reduce((total, assessment) => {
    return total + (assessment.duration || 0)
  }, 0)

  return totalHours
}

// Helper selector to get formatted total hours
export const selectFormattedTotalPracticeHours = (state) => {
  const totalHours = selectTotalPracticeHours(state)

  if (totalHours < 1) {
    const minutes = Math.round(totalHours * 60)
    return `${minutes} min`
  } else {
    const hours = Math.floor(totalHours)
    const minutes = Math.round((totalHours - hours) * 60)
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
  }
}

// Helper selector to get communication assessment count (includes hinglish)
export const selectCommunicationAssessmentCount = state => {
  const completedAssessments = selectCompletedPracticeAssessments(state)
  // Filter for communication and hinglish assessments
  const communicationAssessments = completedAssessments.filter(assessment => {
    const type = (assessment.type || assessment.assessmentType || "").toLowerCase()
    return type === "communication" || type === "hinglish"
  })
  return communicationAssessments.length
}

// Helper selector to get aptitude assessment count
export const selectAptitudeAssessmentCount = state => {
  const completedAssessments = selectCompletedPracticeAssessments(state)
  // Filter for aptitude assessments
  const aptitudeAssessments = completedAssessments.filter(assessment => {
    const type = (assessment.type || assessment.assessmentType || "").toLowerCase()
    return type === "aptitude"
  })
  return aptitudeAssessments.length
}

// Helper selector to get technical assessment count
export const selectTechnicalAssessmentCount = state => {
  const completedAssessments = selectCompletedPracticeAssessments(state)
  // Filter for technical assessments
  const technicalAssessments = completedAssessments.filter(assessment => {
    const type = (assessment.type || assessment.assessmentType || "").toLowerCase()
    return type === "technical"
  })
  return technicalAssessments.length
}

// Add these new selectors for updated counts based on all assessments
export const selectUpdatedCommunicationAssessmentCount = state => {
  const completedAssessments = selectCompletedPracticeAssessments(state)
  return completedAssessments.filter(assessment => {
    const type = (assessment.type || assessment.assessmentType || "").toLowerCase()
    return type === "communication" || type === "hinglish"
  }).length
}

export const selectUpdatedAptitudeAssessmentCount = state => {
  const completedAssessments = selectCompletedPracticeAssessments(state)
  return completedAssessments.filter(assessment =>
    (assessment.type || assessment.assessmentType || "").toLowerCase() === "aptitude"
  ).length
}

export const selectUpdatedTechnicalAssessmentCount = state => {
  const completedAssessments = selectCompletedPracticeAssessments(state)
  return completedAssessments.filter(assessment =>
    (assessment.type || assessment.assessmentType || "").toLowerCase() === "technical"
  ).length
}

// Remove the incorrect selectors at the bottom and replace with these:
export const selectStudentAssessments = (state) =>
  state?.SubscribedInstitutes?.studentAssessments?.assessments || []

export const selectStudentAssessmentsLoading = (state) =>
  state?.SubscribedInstitutes?.studentAssessmentsLoading || false

export const selectStudentAssessmentsError = (state) =>
  state?.SubscribedInstitutes?.studentAssessmentsError || null





// Add these new selectors at the end
export const selectLastAssessment = (state) =>
  state?.SubscribedInstitutes?.lastAssessment?.data || null

export const selectLastAssessmentLoading = (state) =>
  state?.SubscribedInstitutes?.lastAssessment?.loading || false

export const selectLastAssessmentError = (state) =>
  state?.SubscribedInstitutes?.lastAssessment?.error || null

export const selectLastPractice = (state) =>
  state?.SubscribedInstitutes?.lastPractice?.data || null

export const selectLastPracticeLoading = (state) =>
  state?.SubscribedInstitutes?.lastPractice?.loading || false

export const selectLastPracticeError = (state) =>
  state?.SubscribedInstitutes?.lastPractice?.error || null

// Helper selectors for formatted data
export const selectFormattedLastAssessment = (state) => {
  const lastAssessment = selectLastAssessment(state)
  if (!lastAssessment?.practice_last) return null

  return {
    lastDate: lastAssessment.practice_last.last_date,
    daysAgo: lastAssessment.practice_last.ago,
    formattedText: lastAssessment.practice_last.ago === 0
      ? 'Today'
      : lastAssessment.practice_last.ago === 1
        ? '1 day ago'
        : `${lastAssessment.practice_last.ago} days ago`
  }
}

export const selectFormattedLastPractice = (state) => {
  const lastPractice = selectLastPractice(state)
  if (!lastPractice?.practice_last) return null

  return {
    lastDate: lastPractice.practice_last.last_date,
    daysAgo: lastPractice.practice_last.ago,
    formattedText: lastPractice.practice_last.ago === 0
      ? 'Today'
      : lastPractice.practice_last.ago === 1
        ? '1 day ago'
        : `${lastPractice.practice_last.ago} days ago`
  }
}

// Add these new selectors at the end
export const selectCEFRLevel = (state) =>
  state?.SubscribedInstitutes?.cefrLevel?.data || null

export const selectCEFRLevelLoading = (state) =>
  state?.SubscribedInstitutes?.cefrLevel?.loading || false

export const selectCEFRLevelError = (state) =>
  state?.SubscribedInstitutes?.cefrLevel?.error || null

// Helper selector to get CEFR score based on level
export const selectCEFRScore = (state) => {
  const cefrData = selectCEFRLevel(state)

  if (!cefrData || !cefrData.hasCompletedAssessments || !cefrData.maxResultingCEFR) {
    return 0
  }

  const cefrLevel = cefrData.maxResultingCEFR

  const cefrScoreMap = {
    'A1': 0,
    'A2': 31,
    'B1': 60,
    'B2': 80,
    'C1': 90,
    'C2': 95
  }

  return cefrScoreMap[cefrLevel] || 0
}

// Helper selector to get formatted CEFR data
export const selectFormattedCEFRData = (state) => {
  const cefrData = selectCEFRLevel(state)
  const cefrScore = selectCEFRScore(state)

  if (!cefrData) {
    return {
      level: 'Not Available',
      score: 0,
      hasCompleted: false,
      improvement: 0
    }
  }

  return {
    level: cefrData.maxResultingCEFR || 'Not Available',
    score: cefrScore,
    hasCompleted: cefrData.hasCompletedAssessments,
    improvement: cefrScore > 0 ? 20 : 0, // You can adjust this logic based on your needs
    lastAssessmentDate: cefrData.assessmentDetails?.submittedAt || null
  }
}



// Add these new selectors
export const selectAssessmentScores = (state) =>
  state?.SubscribedInstitutes?.assessmentScores || []

export const selectAssessmentScoresLoading = (state) =>
  state?.SubscribedInstitutes?.assessmentScoresLoading || false

export const selectAssessmentScoresError = (state) =>
  state?.SubscribedInstitutes?.assessmentScoresError || null

// Add these new selectors at the end
export const selectReports = (state) =>
  state?.SubscribedInstitutes?.reports?.data || []

export const selectReportsLoading = (state) =>
  state?.SubscribedInstitutes?.reports?.loading || false

export const selectReportsError = (state) =>
  state?.SubscribedInstitutes?.reports?.error || null



export const selectScoreInfo = (state) =>
  state?.SubscribedInstitutes?.scoreInfo || null

export const selectScoreInfoLoading = (state) =>
  state?.SubscribedInstitutes?.scoreInfoLoading || false

export const selectScoreInfoError = (state) =>
  state?.SubscribedInstitutes?.scoreInfoError || null

// Add a new selector specifically for aptitude assessments
export const selectAptitudeAssessmentsCompleted = state => {
  const completedAssessments = selectCompletedPracticeAssessments(state)
  return completedAssessments.filter(assessment => {
    const type = (assessment.type || assessment.assessmentType || "").toLowerCase()
    return type === "aptitude"
  }).length
}

// Add selector to calculate aptitude metrics from assessment history
export const selectAptitudeMetricsFromHistory = (studentAssessmentHistory = []) => {
  const aptitudeAssessments = studentAssessmentHistory.filter(assessment => {
    const type = (assessment.type || "").toLowerCase()
    return type === "aptitude"
  })

  return {
    total: aptitudeAssessments.length,
    completed: aptitudeAssessments.filter(a => a.status === "completed").length,
    pending: aptitudeAssessments.filter(a => a.status === "pending").length,
  }
}

// Add aptitude practice scores selectors
export const selectAptitudePracticeScores2 = (state) =>
  state?.SubscribedInstitutes?.aptitudePracticeScores || { data: [], dayZero: null }

export const selectAptitudePracticeScoresLoading = (state) =>
  state?.SubscribedInstitutes?.aptitudePracticeScores?.loading || false

export const selectAptitudePracticeScoresError = (state) =>
  state?.SubscribedInstitutes?.aptitudePracticeScores?.error || null

export const selectAptitudeDayZero = (state) =>
  state?.SubscribedInstitutes?.aptitudePracticeScores?.dayZero || null

export const selectCommunicationDayZero = (state) =>
  state?.SubscribedInstitutes?.practiceScores?.dayZero || null

// Add actual assessment scores selectors (is_practice=false)
export const selectAssessmentScores2 = (state) => {
  const scores = state.SubscribedInstitutes?.assessmentScores?.data || []
  return scores
}

export const selectAssessmentScores2Loading = (state) => {
  const loading = state.SubscribedInstitutes?.assessmentScores?.loading || false
  return loading
}

export const selectAssessmentScores2Error = (state) => {
  const error = state.SubscribedInstitutes?.assessmentScores?.error || null
  return error
}

export const selectCommunicationAssessmentDayZero = (state) =>
  state?.SubscribedInstitutes?.assessmentScores?.dayZero || null

// Add actual aptitude assessment scores selectors (is_practice=false)
export const selectAptitudeAssessmentScores2 = (state) =>
  state?.SubscribedInstitutes?.aptitudeAssessmentScores || { data: [], dayZero: null }

export const selectAptitudeAssessmentScores2Loading = (state) =>
  state?.SubscribedInstitutes?.aptitudeAssessmentScores?.loading || false

export const selectAptitudeAssessmentScores2Error = (state) =>
  state?.SubscribedInstitutes?.aptitudeAssessmentScores?.error || null

export const selectAptitudeAssessmentDayZero = (state) =>
  state?.SubscribedInstitutes?.aptitudeAssessmentScores?.dayZero || null

// Practice-only assessment count selectors
export const selectPracticeOnlyCommunicationCount = (state) => {
  const practiceAssessments = state?.SubscribedInstitutes?.completedPracticeAssessments?.data || []
  return practiceAssessments.filter(assessment => {
    const type = (assessment.type || assessment.assessmentType || '').toLowerCase()
    return type === 'communication' || type === 'hinglish'
  }).length
}

export const selectPracticeOnlyAptitudeCount = (state) => {
  const practiceAssessments = state?.SubscribedInstitutes?.completedPracticeAssessments?.data || []
  return practiceAssessments.filter(
    assessment => (assessment.type || assessment.assessmentType || '').toLowerCase() === 'aptitude'
  ).length
}

export const selectPracticeOnlyTechnicalCount = (state) => {
  const practiceAssessments = state?.SubscribedInstitutes?.completedPracticeAssessments?.data || []
  return practiceAssessments.filter(
    assessment => (assessment.type || assessment.assessmentType || '').toLowerCase() === 'technical'
  ).length
}

export const selectPracticeOnlyTotalHours = (state) => {
  const practiceAssessments = state?.SubscribedInstitutes?.completedPracticeAssessments?.data || []
  const totalMinutes = practiceAssessments.reduce((sum, assessment) => {
    const duration = Number(assessment.duration || 0)
    return sum + duration
  }, 0)
  const totalHours = totalMinutes / 60
  return totalHours.toFixed(2)
}

export const selectTpoStudentList = (state) =>
  state.SubscribedInstitutes.tpoStudentList || []

export const selectTpoStudentListPagination = (state) =>
  state.SubscribedInstitutes.tpoStudentListPagination || {
    totalCount: 0,
    pageNumber: 1,
    pageSize: 10,
  }

export const selectTpoStudentListLoading = (state) =>
  state.SubscribedInstitutes.tpoStudentListLoading || false

export const selectSpecificAssessmentStudentList = state => {
  const list = state?.SubscribedInstitutes?.specificAssessmentStudentList
  console.log('selectSpecificAssessmentStudentList RAW STATE:', list)
  return list?.data || []
}

export const selectSpecificAssessmentStudentListPagination = createSelector(
  [state => state?.SubscribedInstitutes?.specificAssessmentStudentList?.totalCount],
  (totalCount) => ({
    total: totalCount || 0,
  })
)

export const selectSpecificAssessmentStudentListLoading = state =>
  state?.SubscribedInstitutes?.specificAssessmentStudentList?.loading || false

// Diagnosis data selectors
export const selectDiagnosisData = state =>
  state?.SubscribedInstitutes?.diagnosisData || {
    students: [],
    totalCount: 0,
    completedDiagnosisCount: 0,
    pageNumber: 1,
    pageSize: 10,
    assessmentType: null,
    loading: false,
    error: null,
  }

export const selectDiagnosisStudents = state =>
  state?.SubscribedInstitutes?.diagnosisData?.students || []

export const selectDiagnosisLoading = state =>
  state?.SubscribedInstitutes?.diagnosisData?.loading || false

export const selectDiagnosisError = state =>
  state?.SubscribedInstitutes?.diagnosisData?.error || null

export const selectDiagnosisTotalCount = state =>
  state?.SubscribedInstitutes?.diagnosisData?.totalCount || 0

export const selectDiagnosisCompletedCount = state =>
  state?.SubscribedInstitutes?.diagnosisData?.completedDiagnosisCount || 0

export const selectDiagnosisPagination = state => ({
  pageNumber: state?.SubscribedInstitutes?.diagnosisData?.pageNumber || 1,
  pageSize: state?.SubscribedInstitutes?.diagnosisData?.pageSize || 10,
  totalCount: state?.SubscribedInstitutes?.diagnosisData?.totalCount || 0,
})

export const selectDiagnosisAssessmentType = state =>
  state?.SubscribedInstitutes?.diagnosisData?.assessmentType || null